/**
 * SynthesisPanel — AI writing assistant using formr-lib + selectr-ui + widgetr-lib.
 *
 * Composes:
 * - formr-lib: Form configuration types (FieldConfig, FormConfig)
 * - selectr-ui (Selectr): Tone/audience/format/length pickers
 * - widgetr-lib (Input, Dropdown): Form fields
 * - expressr-lib: synthesize() via context
 *
 * The synthesis options (tone, audience, format, length) map directly
 * to expressr-lib's SynthesisOptions. The form structure follows
 * formr-lib patterns.
 */

import React, { useState, useCallback } from 'react';
import { useExpressrContext } from '../context/ExpressrContext.js';
import type { SynthesisOptions } from '../types.js';

interface SynthesisFormState {
  topic: string;
  sourceContent: string;
  tone: NonNullable<SynthesisOptions['tone']>;
  audience: NonNullable<SynthesisOptions['audience']>;
  format: NonNullable<SynthesisOptions['format']>;
  length: NonNullable<SynthesisOptions['length']>;
  theme: string;
  instructions: string;
}

// selectr-ui items for each picker
const TONE_ITEMS = [
  { id: 'professional', label: 'Professional' },
  { id: 'casual', label: 'Casual' },
  { id: 'persuasive', label: 'Persuasive' },
  { id: 'educational', label: 'Educational' },
  { id: 'creative', label: 'Creative' },
  { id: 'philosophical', label: 'Philosophical' },
];

const AUDIENCE_ITEMS = [
  { id: 'executive', label: 'Executive' },
  { id: 'technical', label: 'Technical' },
  { id: 'general', label: 'General' },
  { id: 'investor', label: 'Investor' },
  { id: 'partner', label: 'Partner' },
  { id: 'internal', label: 'Internal' },
  { id: 'public', label: 'Public' },
];

const LENGTH_ITEMS = [
  { id: 'brief', label: 'Brief (~250 words)' },
  { id: 'standard', label: 'Standard (~750 words)' },
  { id: 'comprehensive', label: 'Comprehensive (~2000 words)' },
];

const FORMAT_ITEMS = [
  { id: 'markdown', label: 'Markdown' },
  { id: 'html', label: 'HTML' },
  { id: 'plain', label: 'Plain Text' },
];

// Presets: pre-configured combinations
const PRESETS = [
  { label: 'Blog Post', tone: 'casual' as const, audience: 'general' as const, length: 'standard' as const },
  { label: 'Investor Pitch', tone: 'persuasive' as const, audience: 'investor' as const, length: 'brief' as const },
  { label: 'Technical Doc', tone: 'educational' as const, audience: 'technical' as const, length: 'comprehensive' as const },
  { label: 'Exec Summary', tone: 'professional' as const, audience: 'executive' as const, length: 'brief' as const },
  { label: 'Team Update', tone: 'casual' as const, audience: 'internal' as const, length: 'standard' as const },
  { label: 'Thought Piece', tone: 'philosophical' as const, audience: 'general' as const, length: 'comprehensive' as const },
];

/**
 * SynthesisPanel renders the AI writing assistant form.
 *
 * In full integration:
 * - Each picker would use <Selectr items={...} value={...} onChange={...} />
 * - Text fields would use <Input from widgetr-lib />
 * - The form structure would follow formr-lib FormConfig
 */
export function SynthesisPanel() {
  const { synthesize, insertSynthesis, activeDocument, state, config } = useExpressrContext();

  const [form, setForm] = useState<SynthesisFormState>({
    topic: '',
    sourceContent: '',
    tone: 'professional',
    audience: 'general',
    format: 'markdown',
    length: 'standard',
    theme: '',
    instructions: '',
  });

  const [resultContent, setResultContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateForm = useCallback(<K extends keyof SynthesisFormState>(key: K, value: SynthesisFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyPreset = useCallback((preset: typeof PRESETS[number]) => {
    setForm((prev) => ({ ...prev, tone: preset.tone, audience: preset.audience, length: preset.length }));
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!form.topic.trim()) { setError('Topic is required'); return; }
    setError(null);
    try {
      const options: SynthesisOptions = {
        topic: form.topic,
        sourceContent: form.sourceContent.trim() ? form.sourceContent.split('\n---\n').filter(Boolean) : undefined,
        tone: form.tone,
        audience: form.audience,
        format: form.format,
        length: form.length,
        theme: form.theme || undefined,
        instructions: form.instructions || undefined,
      };
      const result = await synthesize(options);
      setResultContent(result.content);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Synthesis failed');
    }
  }, [form, synthesize]);

  const handleInsert = useCallback(() => {
    if (resultContent) { insertSynthesis(resultContent); setResultContent(null); }
  }, [resultContent, insertSynthesis]);

  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, opacity: 0.6 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #2a2a4a)' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>AI Synthesis</span>
        {!config?.llm && (
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7 }}>
            No LLM provider configured. Connect assistr-lib to enable.
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        {/* Presets */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Quick Presets</span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {PRESETS.map((p) => <button key={p.label} onClick={() => applyPreset(p)}>{p.label}</button>)}
          </div>
        </div>

        {/* Topic — widgetr Input */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Topic *</span>
          <input type="text" value={form.topic} onChange={(e) => updateForm('topic', e.target.value)} placeholder="What should this be about?" style={{ width: '100%' }} />
        </div>

        {/* Tone — selectr-ui Selectr */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Tone</span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {TONE_ITEMS.map((t) => <button key={t.id} data-active={form.tone === t.id} onClick={() => updateForm('tone', t.id as any)}>{t.label}</button>)}
          </div>
        </div>

        {/* Audience — selectr-ui Selectr */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Audience</span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {AUDIENCE_ITEMS.map((a) => <button key={a.id} data-active={form.audience === a.id} onClick={() => updateForm('audience', a.id as any)}>{a.label}</button>)}
          </div>
        </div>

        {/* Length — selectr-ui Selectr */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Length</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {LENGTH_ITEMS.map((l) => <button key={l.id} data-active={form.length === l.id} onClick={() => updateForm('length', l.id as any)} style={{ flex: 1 }}>{l.label}</button>)}
          </div>
        </div>

        {/* Format — selectr-ui Selectr */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Format</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {FORMAT_ITEMS.map((f) => <button key={f.id} data-active={form.format === f.id} onClick={() => updateForm('format', f.id as any)}>{f.label}</button>)}
          </div>
        </div>

        {/* Theme — widgetr Input */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Theme / Angle</span>
          <input type="text" value={form.theme} onChange={(e) => updateForm('theme', e.target.value)} placeholder="e.g., Empowerment through transparency" style={{ width: '100%' }} />
        </div>

        {/* Source material — widgetr textarea */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Source Material</span>
          <textarea value={form.sourceContent} onChange={(e) => updateForm('sourceContent', e.target.value)} placeholder="Paste sources. Separate with ---" rows={4} style={{ width: '100%' }} />
          {activeDocument && activeDocument.content.length > 0 && (
            <button onClick={() => updateForm('sourceContent', form.sourceContent ? `${form.sourceContent}\n---\n${activeDocument.content}` : activeDocument.content)} style={{ marginTop: 4, fontSize: 11 }}>
              + Use current document as source
            </button>
          )}
        </div>

        {/* Instructions — widgetr textarea */}
        <div style={{ marginBottom: 14 }}>
          <span style={labelStyle}>Instructions</span>
          <textarea value={form.instructions} onChange={(e) => updateForm('instructions', e.target.value)} placeholder="Additional guidance..." rows={2} style={{ width: '100%' }} />
        </div>

        {error && <div style={{ color: 'red', fontSize: 12, marginBottom: 8 }}>{error}</div>}

        <button onClick={handleGenerate} disabled={state.generating || !form.topic.trim()} style={{ width: '100%', padding: '8px 16px', fontWeight: 600 }}>
          {state.generating ? 'Generating...' : 'Generate Content'}
        </button>

        {/* Result */}
        {resultContent && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={labelStyle}>Result</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={handleInsert}>Insert into Document</button>
                <button onClick={() => navigator.clipboard?.writeText(resultContent)}>Copy</button>
              </div>
            </div>
            <pre style={{ fontSize: 12, lineHeight: 1.6, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap' }}>{resultContent}</pre>
          </div>
        )}
      </div>
    </div>
  );
}